import JSZip from 'jszip';
import { DownloadError } from './downloadError';
import { DOWNLOAD_MESSAGES } from './downloadMessages';
import {
  fetchInstanceDicomBytes,
  YIELD_EVERY_N_INSTANCES,
  yieldToMainThread,
} from './fetchInstanceDicomBytes';
import { buildZipEntryPath } from './buildStudyDicomZipFilename';
import type { DicomZipProgressCallback, StudyDicomInstance } from './types';

export async function buildStudyDicomZip(
  instances: StudyDicomInstance[],
  onProgress?: DicomZipProgressCallback
): Promise<{ blob: Blob; includedCount: number }> {
  const zip = new JSZip();
  const usedEntryPaths = new Set<string>();
  let includedCount = 0;

  for (let index = 0; index < instances.length; index += 1) {
    const instance = instances[index];

    try {
      const arrayBuffer = await fetchInstanceDicomBytes(instance);
      let entryPath = buildZipEntryPath(instance);

      if (usedEntryPaths.has(entryPath)) {
        entryPath = `${entryPath.replace(/\.dcm$/, '')}_${includedCount + 1}.dcm`;
      }

      usedEntryPaths.add(entryPath);
      zip.file(entryPath, arrayBuffer, { compression: 'STORE' });
      includedCount += 1;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[ViewportDownload] Skipping instance during ZIP export:', instance, error);
      }
    }

    onProgress?.(index + 1, instances.length);

    if ((index + 1) % YIELD_EVERY_N_INSTANCES === 0) {
      await yieldToMainThread();
    }
  }

  if (!includedCount) {
    return { blob: new Blob(), includedCount: 0 };
  }

  await yieldToMainThread();

  try {
    const blob = await zip.generateAsync({
      type: 'blob',
      compression: 'STORE',
    });

    if (!blob || blob.size === 0) {
      throw new DownloadError(DOWNLOAD_MESSAGES.ZIP_GENERATION_FAILED);
    }

    return { blob, includedCount };
  } catch (error) {
    if (error instanceof DownloadError) {
      throw error;
    }

    throw new DownloadError(DOWNLOAD_MESSAGES.ZIP_GENERATION_FAILED, error);
  }
}
